import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Link } from '@inertiajs/react'
import { Eye, EyeOff } from 'lucide-react'

interface AppSecret {
  id: number
  key: string
  value: string
  created_at: string
  updated_at: string
}

interface DevInstance {
  id: number
  name: string
}

interface AdminDevInstanceAppSecretsProps {
  dev_instance: DevInstance
  project_id: number
  app_secrets: AppSecret[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function AppSecrets({ dev_instance, project_id, app_secrets }: AdminDevInstanceAppSecretsProps) {
  const { t } = useTranslation('admin')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editKey, setEditKey] = useState('')
  const [editValue, setEditValue] = useState('')
  const [_originalKey, setOriginalKey] = useState('')
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [vpsSecretsContent, setVpsSecretsContent] = useState<string | null>(null)
  const [loadingVpsSecrets, setLoadingVpsSecrets] = useState(false)
  const [vpsSecretsError, setVpsSecretsError] = useState<string | null>(null)

  const createForm = useForm({
    key: '',
    value: '',
    is_live: false
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createForm.post(`/projects/${project_id}/environment_variables`, {
      preserveScroll: true,
      onSuccess: () => {
        createForm.reset()
      }
    })
  }

  const handleUpdate = (id: number) => {
    router.patch(`/projects/${project_id}/environment_variables/${id}`, {
      key: editKey,
      value: editValue
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setEditingId(null)
        setEditKey('')
        setEditValue('')
        setOriginalKey('')
      }
    })
  }

  const handleDelete = (id: number, key: string) => {
    if (confirm(t('devInstances.appSecrets.deleteConfirm', { key }))) {
      router.delete(`/projects/${project_id}/environment_variables/${id}`, {
        preserveScroll: true
      })
    }
  }

  const startEdit = (variable: AppSecret) => {
    setEditingId(variable.id)
    setOriginalKey(variable.key)
    setEditKey(variable.key)
    setEditValue(variable.value)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setOriginalKey('')
    setEditKey('')
    setEditValue('')
  }

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleFetchVpsSecrets = async () => {
    setLoadingVpsSecrets(true)
    setVpsSecretsError(null)

    try {
      const response = await fetch(`/admin/dev_instances/${dev_instance.id}/fetch_vps_app_secrets`)
      const data = await response.json()

      if (!response.ok) {
        setVpsSecretsError(data.error || 'Failed to fetch VPS app secrets')
        setVpsSecretsContent(null)
      } else {
        setVpsSecretsContent(data.contents)
        setVpsSecretsError(null)
      }
    } catch (error) {
      setVpsSecretsError('Network error while fetching VPS app secrets')
      setVpsSecretsContent(null)
    } finally {
      setLoadingVpsSecrets(false)
    }
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('devInstances.index.title'), href: '/admin/dev_instances' }, { label: t('devInstances.appSecrets.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('devInstances.appSecrets.title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('devInstances.appSecrets.description', { name: dev_instance.name })}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href={`/admin/dev_instances/${dev_instance.id}`}>{t('devInstances.appSecrets.backButton')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* App Secrets List */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground">{t('devInstances.appSecrets.currentSecrets.title')}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('devInstances.appSecrets.currentSecrets.count', { count: app_secrets.length })}
                </p>
              </div>

              {app_secrets.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('devInstances.appSecrets.table.key')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('devInstances.appSecrets.table.value')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('devInstances.appSecrets.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {app_secrets.map((variable) => (
                        <tr key={variable.id} className="hover:bg-muted">
                          {editingId === variable.id ? (
                            <>
                              <td className="px-6 py-4">
                                <Input
                                  type="text"
                                  value={editKey}
                                  onChange={(e) => setEditKey(e.target.value.toUpperCase())}
                                  className="font-mono text-sm"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <Input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="font-mono text-sm"
                                />
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex gap-2 justify-end">
                                  <Button size="sm" onClick={() => handleUpdate(variable.id)}>
                                    {t('devInstances.appSecrets.table.save')}
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={cancelEdit}>
                                    {t('devInstances.appSecrets.table.cancel')}
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4">
                                <span className="text-sm font-medium text-foreground font-mono">{variable.key}</span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground font-mono">
                                    {visibleValues.has(variable.id) ? variable.value : '••••••••'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => toggleValueVisibility(variable.id)}
                                    className="text-gray-400 hover:text-muted-foreground"
                                  >
                                    {visibleValues.has(variable.id) ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => startEdit(variable)}
                                  >
                                    {t('devInstances.appSecrets.table.edit')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDelete(variable.id, variable.key)}
                                  >
                                    {t('devInstances.appSecrets.table.delete')}
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-8">
                  <p className="text-sm text-muted-foreground text-center">
                    {t('devInstances.appSecrets.currentSecrets.empty')}
                  </p>
                </div>
              )}
            </div>

            {/* VPS .env File Viewer */}
            <div className="bg-card rounded-lg border border-border overflow-hidden mt-6">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{t('devInstances.appSecrets.vpsEnv.title')}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('devInstances.appSecrets.vpsEnv.description')}
                  </p>
                </div>
                <Button
                  onClick={handleFetchVpsSecrets}
                  disabled={loadingVpsSecrets}
                  variant="outline"
                >
                  {loadingVpsSecrets ? t('devInstances.appSecrets.vpsEnv.loading') : t('devInstances.appSecrets.vpsEnv.viewButton')}
                </Button>
              </div>

              {vpsSecretsError && (
                <div className="px-6 py-4 bg-destructive/10 text-destructive">
                  <p className="text-sm">{vpsSecretsError}</p>
                </div>
              )}

              {vpsSecretsContent && (
                <div className="px-6 py-4">
                  <pre className="text-sm font-mono bg-muted p-4 rounded-md overflow-x-auto whitespace-pre">
                    {vpsSecretsContent}
                  </pre>
                </div>
              )}

              {!vpsSecretsContent && !vpsSecretsError && !loadingVpsSecrets && (
                <div className="px-6 py-8">
                  <p className="text-sm text-muted-foreground text-center">
                    {t('devInstances.appSecrets.vpsEnv.empty')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Add New Secret */}
          <div>
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('devInstances.appSecrets.addSecret.title')}</CardTitle>
                <CardDescription>{t('devInstances.appSecrets.addSecret.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="key">{t('devInstances.appSecrets.addSecret.keyLabel')}</Label>
                    <Input
                      id="key"
                      type="text"
                      placeholder={t('devInstances.appSecrets.addSecret.keyPlaceholder')}
                      value={createForm.data.key}
                      onChange={(e) => createForm.setData('key', e.target.value.toUpperCase())}
                      required
                      className="font-mono"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value">{t('devInstances.appSecrets.addSecret.valueLabel')}</Label>
                    <Input
                      id="value"
                      type="text"
                      placeholder={t('devInstances.appSecrets.addSecret.valuePlaceholder')}
                      value={createForm.data.value}
                      onChange={(e) => createForm.setData('value', e.target.value)}
                      required
                      className="font-mono"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={createForm.processing}>
                    {createForm.processing ? t('devInstances.appSecrets.addSecret.adding') : t('devInstances.appSecrets.addSecret.button')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}




AppSecrets.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default AppSecrets
